import { supabase } from '../lib/supabase';

export interface SingAlongPass {
  id: string;
  name: string;
  duration_hours: number;
  bundle_limit: number | null;
  price_cents: number;
  description: string | null;
  tag: string | null;
  sort_order: number;
}

export interface SingAlongBundle {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  song_count?: number;
  songs?: { id: string; title: string; artist: string; cover_url: string | null }[];
}

export interface SingAlongPurchase {
  id: string;
  user_id: string;
  pass_id: string;
  purchased_at: string;
  expires_at: string;
  status: string;
  league_id: string | null;
  pass?: SingAlongPass;
}

export interface SingAlongArchive {
  id: string;
  purchase_id: string;
  league_id: string;
  archived_at: string;
  archive_data: {
    participants: { display_name: string; score: number }[];
    songs: { title: string; artist: string }[];
    submission_count: number;
  };
}

export const SingAlongService = {
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getAccountDestination(basePath: string = 'sing-along'): Promise<string> {
    const purchase = await this.getActivePurchase();
    if (!purchase) return `/${basePath}/plans`;
    return purchase.league_id ? `/${basePath}/dashboard` : `/${basePath}/setup`;
  },

  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { singalong_user: true } },
    });
    if (error) throw error;
    return data;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async sendVerification(email: string, basePath: string = 'sing-along', leagueSlug?: string): Promise<{ debug_code?: string }> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/send-singalong-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        redirect_url: `${window.location.origin}/${basePath}/verify`,
        league_slug: leagueSlug,
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to send verification email');
    }
    return res.json();
  },

  async verifyEmail(email: string, code: string): Promise<void> {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/verify-singalong-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Verification failed');
    }
  },

  async resendVerification(email: string, basePath: string = 'sing-along', leagueSlug?: string): Promise<{ debug_code?: string }> {
    return this.sendVerification(email, basePath, leagueSlug);
  },

  async getPasses(): Promise<SingAlongPass[]> {
    const { data, error } = await supabase
      .from('singalong_passes')
      .select('id, name, duration_hours, bundle_limit, price_cents, description, tag, sort_order')
      .eq('is_active', true)
      .order('sort_order');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async getBundles(): Promise<SingAlongBundle[]> {
    const { data, error } = await supabase
      .from('singalong_bundles')
      .select(`
        id, name, description, is_active,
        singalong_bundle_songs(
          song_id,
          songs(id, title, artist, cover_url)
        )
      `)
      .eq('is_active', true)
      .order('name');
    if (error) throw new Error(error.message);
    return (data || []).map((b: any) => ({
      id: b.id,
      name: b.name,
      description: b.description,
      is_active: b.is_active,
      song_count: b.singalong_bundle_songs?.length || 0,
      songs: b.singalong_bundle_songs?.map((bs: any) => bs.songs).filter(Boolean) || [],
    }));
  },

  async getActivePurchase(): Promise<SingAlongPurchase | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('singalong_purchases')
      .select(`
        id, user_id, pass_id, purchased_at, expires_at, status, league_id,
        singalong_passes(id, name, duration_hours, bundle_limit, price_cents, description, tag, sort_order)
      `)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .gt('expires_at', new Date().toISOString())
      .order('purchased_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!data) return null;

    return {
      id: data.id,
      user_id: data.user_id,
      pass_id: data.pass_id,
      purchased_at: data.purchased_at,
      expires_at: data.expires_at,
      status: data.status,
      league_id: data.league_id,
      pass: (data as any).singalong_passes || undefined,
    };
  },

  async getAllPurchases(): Promise<SingAlongPurchase[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('singalong_purchases')
      .select(`
        id, user_id, pass_id, purchased_at, expires_at, status, league_id,
        singalong_passes(id, name, duration_hours, bundle_limit, price_cents, description, tag, sort_order)
      `)
      .eq('user_id', user.id)
      .order('purchased_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []).map((d: any) => ({
      ...d,
      pass: d.singalong_passes || undefined,
    }));
  },

  async createCheckout(passId: string, basePath: string = 'sing-along'): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const baseUrl = window.location.origin;
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-singalong-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          pass_id: passId,
          success_url: `${baseUrl}/${basePath}/setup?success=true`,
          cancel_url: `${baseUrl}/${basePath}/plans?canceled=true`,
        }),
      }
    );

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Checkout failed');
    }

    const { url } = await res.json();
    return url;
  },

  async devPurchase(passId: string): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: pass, error: passError } = await supabase
      .from('singalong_passes')
      .select('duration_hours')
      .eq('id', passId)
      .single();
    if (passError || !pass) throw new Error('Pass not found');

    const expiresAt = new Date(Date.now() + pass.duration_hours * 60 * 60 * 1000).toISOString();

    const { data: purchase, error } = await supabase
      .from('singalong_purchases')
      .insert({
        user_id: user.id,
        pass_id: passId,
        expires_at: expiresAt,
        status: 'active',
      })
      .select('id')
      .single();

    if (error) throw new Error(error.message);
    return purchase.id;
  },

  async checkSlugAvailable(slug: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('id')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return !data;
  },

  async createKiosk(params: {
    purchaseId: string;
    name: string;
    slug: string;
    passcode: string;
    bundleIds: string[];
  }): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: league, error: leagueError } = await supabase
      .from('kiosk_leagues')
      .insert({
        name: params.name,
        slug: params.slug,
        status: 'open',
        signup_mode: 'passcode',
        passcode: params.passcode,
        owner_user_id: user.id,
        scoring_mode: 'best',
        allow_playback_default: true,
        primary_color: '#f97316',
        secondary_color: '#3b82f6',
        accent_color: '#f59e0b',
      })
      .select('id')
      .single();

    if (leagueError) throw new Error(leagueError.message);

    const { error: adminError } = await supabase
      .from('kiosk_league_admins')
      .insert({
        league_id: league.id,
        user_id: user.id,
        role: 'owner',
      });
    if (adminError) throw new Error(adminError.message);

    const { error: purchaseError } = await supabase
      .from('singalong_purchases')
      .update({ league_id: league.id })
      .eq('id', params.purchaseId);
    if (purchaseError) throw new Error(purchaseError.message);

    if (params.bundleIds.length > 0) {
      const { error: bundleError } = await supabase
        .from('singalong_purchase_bundles')
        .insert(params.bundleIds.map(bid => ({ purchase_id: params.purchaseId, bundle_id: bid })));
      if (bundleError) throw new Error(bundleError.message);
    }

    const songQuery = params.bundleIds.length > 0
      ? supabase
          .from('singalong_bundle_songs')
          .select('song_id, display_order')
          .in('bundle_id', params.bundleIds)
      : supabase
          .from('singalong_bundle_songs')
          .select('song_id, display_order');

    const { data: bundleSongs, error: bsError } = await songQuery;
    if (bsError) throw new Error(bsError.message);

    const uniqueSongs = [...new Map((bundleSongs || []).map(s => [s.song_id, s])).values()];

    if (uniqueSongs.length > 0) {
      const { error: leagueSongsError } = await supabase
        .from('kiosk_league_songs')
        .insert(uniqueSongs.map((s, i) => ({
          league_id: league.id,
          song_id: s.song_id,
          display_order: i,
        })));
      if (leagueSongsError) throw new Error(leagueSongsError.message);
    }

    return league.id;
  },

  async getOwnerLeague(purchaseId: string) {
    const { data, error } = await supabase
      .from('singalong_purchases')
      .select('league_id')
      .eq('id', purchaseId)
      .single();
    if (error) throw new Error(error.message);
    return data?.league_id;
  },

  async getLeagueDetails(leagueId: string) {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('*')
      .eq('id', leagueId)
      .single();
    if (error) throw new Error(error.message);
    return data;
  },

  async getParticipants(leagueId: string) {
    const { data, error } = await supabase
      .from('kiosk_participants')
      .select('id, display_name, status, joined_at, allow_playback, kiosk_user:kiosk_user_id(id, email, marketing_opt_in)')
      .eq('league_id', leagueId)
      .order('joined_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async updatePasscode(leagueId: string, passcode: string) {
    const { error } = await supabase
      .from('kiosk_leagues')
      .update({ passcode })
      .eq('id', leagueId);
    if (error) throw new Error(error.message);
  },

  async updateLeagueName(leagueId: string, name: string) {
    const { error } = await supabase
      .from('kiosk_leagues')
      .update({ name })
      .eq('id', leagueId);
    if (error) throw new Error(error.message);
  },

  async removeParticipant(participantId: string) {
    const { error } = await supabase
      .from('kiosk_participants')
      .delete()
      .eq('id', participantId);
    if (error) throw new Error(error.message);
  },

  async resetKiosk(purchaseId: string, leagueId: string): Promise<void> {
    const { data: participants } = await supabase
      .from('kiosk_participants')
      .select('display_name')
      .eq('league_id', leagueId);

    const { data: submissions } = await supabase
      .from('kiosk_submissions')
      .select('score, participant_id, song_id, songs(title, artist)')
      .eq('league_id', leagueId);

    const { data: leaderboard } = await supabase
      .rpc('kiosk_leaderboard_public', { p_league_slug: '' })
      .limit(0);

    const archiveData = {
      participants: (participants || []).map(p => ({ display_name: p.display_name, score: 0 })),
      songs: [],
      submission_count: submissions?.length || 0,
    };

    if (submissions && submissions.length > 0) {
      const participantScores: Record<string, number> = {};
      for (const s of submissions) {
        const pid = s.participant_id;
        if (!participantScores[pid] || s.score > participantScores[pid]) {
          participantScores[pid] = s.score;
        }
      }
      archiveData.participants = (participants || []).map(p => ({
        display_name: p.display_name,
        score: 0,
      }));

      const songSet = new Set<string>();
      for (const s of submissions) {
        const song = (s as any).songs;
        if (song) songSet.add(JSON.stringify({ title: song.title, artist: song.artist }));
      }
      archiveData.songs = [...songSet].map(s => JSON.parse(s));
    }

    await supabase.from('singalong_archives').insert({
      purchase_id: purchaseId,
      league_id: leagueId,
      archive_data: archiveData,
    });

    await supabase.from('kiosk_submissions').delete().eq('league_id', leagueId);
    await supabase.from('kiosk_participants').delete().eq('league_id', leagueId);
  },

  async getArchives(purchaseId: string): Promise<SingAlongArchive[]> {
    const { data, error } = await supabase
      .from('singalong_archives')
      .select('*')
      .eq('purchase_id', purchaseId)
      .order('archived_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async isKioskExpired(leagueId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('singalong_purchases')
      .select('expires_at')
      .eq('league_id', leagueId)
      .maybeSingle();
    if (error || !data) return true;
    return new Date(data.expires_at) < new Date();
  },

  // Admin bundle operations
  async adminLoadBundles() {
    const { data, error } = await supabase
      .from('singalong_bundles')
      .select(`
        id, name, description, is_active, created_at,
        singalong_bundle_songs(id, song_id, songs(id, title, artist))
      `)
      .order('name');
    if (error) throw new Error(error.message);
    return (data || []).map((b: any) => ({
      ...b,
      songs: (b.singalong_bundle_songs || []).map((bs: any) => ({
        id: bs.id,
        song_id: bs.song_id,
        title: bs.songs?.title || 'Unknown',
        artist: bs.songs?.artist || 'Unknown',
      })),
    }));
  },

  async adminCreateBundle(name: string, description: string | null) {
    const { error } = await supabase.from('singalong_bundles').insert({
      name,
      description,
      is_active: false,
    });
    if (error) throw new Error(error.message);
  },

  async adminToggleBundleActive(bundleId: string, currentState: boolean) {
    const { error } = await supabase
      .from('singalong_bundles')
      .update({ is_active: !currentState })
      .eq('id', bundleId);
    if (error) throw new Error(error.message);
  },

  async adminDeleteBundle(bundleId: string) {
    const { error } = await supabase.from('singalong_bundles').delete().eq('id', bundleId);
    if (error) throw new Error(error.message);
  },

  async adminGetAllSongs() {
    const { data, error } = await supabase
      .from('songs')
      .select('id, title, artist, cover_url')
      .eq('is_deleted', false)
      .eq('hide_from_singalong', false)
      .order('title');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async adminAddSongToBundle(bundleId: string, songId: string, displayOrder: number) {
    const { error } = await supabase.from('singalong_bundle_songs').insert({
      bundle_id: bundleId,
      song_id: songId,
      display_order: displayOrder,
    });
    if (error) throw new Error(error.message);
  },

  async adminRemoveSongFromBundle(bundleSongId: string) {
    const { error } = await supabase.from('singalong_bundle_songs').delete().eq('id', bundleSongId);
    if (error) throw new Error(error.message);
  },

  async sendInvites(opts: { emails: string[]; subject: string; body: string; leagueName: string; leagueSlug?: string }) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token || supabaseAnonKey;

    const res = await fetch(`${supabaseUrl}/functions/v1/send-singalong-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        emails: opts.emails,
        subject: opts.subject,
        body: opts.body,
        league_name: opts.leagueName,
        league_slug: opts.leagueSlug,
      }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Failed to send invites');
    return result;
  },
};
